import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Typography } from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import BranchSelectFilter from "../components/common/BranchSelectFilter";
import AppButton from "../components/buttons/AppButton";
import StandardTable, { TableColumn } from "../components/common/StandardTable";
import ConfirmDialog from "../components/common/ConfirmDialog";
import RowActionMenu, {
  RowActionDef,
} from "../components/common/RowActionMenu";

import CreateTeacherModal from "../components/teachers/CreateTeacherModal";
import UpdateTeacherModal from "../components/teachers/UpdateTeacherModal";
import ViewTeacherModal from "../components/teachers/ViewTeacherModal";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getTeachers,
  getTeacherById,
  deleteTeacher,
  resetTeacherServerStatus,
} from "../redux/actions/teacherActions";
import { TeacherSummaryDto } from "../shared/dtos/teacher/TeacherSummaryDto";
import { TeacherResponseDto } from "../shared/dtos/teacher/TeacherResponseDto";

const TeachersPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items: teachers,
    item: teacher,
    loadingFetchAll: loadingTeachers,
    loadingFetchOne: loadingTeacher,
    errorFetchAll: errorLoadingTeachers,
    errorFetchOne: errorLoadingTeacher,
    pagination,
    serverStatus,
    loadingCreate: loadingTeacherCreate,
    errorCreate: errorCreatingTeacher,
    loadingUpdate: loadingTeacherUpdate,
    errorUpdate: errorUpdatingTeacher,
    loadingDelete: loadingTeacherDelete,
  } = useAppSelector((state) => state.teacher);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  // Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [selectedTeacher, setSelectedTeacher] =
    useState<TeacherSummaryDto | null>(null);

  const canCreate = hasPermission("teacher:create");
  const canUpdate = hasPermission("teacher:update");
  const canDelete = hasPermission("teacher:delete");
  const canRead = hasPermission("teacher:read");

  const fetchTeachersList = useCallback((): void => {
    dispatch(
      getTeachers({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch.trim() || undefined,
        branchId: selectedBranchId || undefined,
      }),
    );
  }, [
    dispatch,
    pageConfig.page,
    pageConfig.limit,
    debouncedSearch,
    selectedBranchId,
  ]);

  useEffect(() => {
    fetchTeachersList();
  }, [fetchTeachersList]);

  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback((): void => {
    if (!loadingTeacherCreate) setIsCreateModalOpen(false);
  }, [loadingTeacherCreate]);

  const handleOpenUpdate = (teacherRow: TeacherSummaryDto): void => {
    dispatch(getTeacherById(teacherRow.id));
    setSelectedTeacher(teacherRow);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback((): void => {
    if (!loadingTeacherUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedTeacher(null);
    }
  }, [loadingTeacherUpdate]);

  const handleOpenDelete = (teacherRow: TeacherSummaryDto): void => {
    setSelectedTeacher(teacherRow);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback((): void => {
    if (!loadingTeacherDelete) {
      setIsDeleteModalOpen(false);
      setSelectedTeacher(null);
    }
  }, [loadingTeacherDelete]);

  const handleOpenView = (teacherRow: TeacherSummaryDto): void => {
    dispatch(getTeacherById(teacherRow.id));
    setIsViewModalOpen(true);
  };
  const handleCloseView = (): void => setIsViewModalOpen(false);

  const handleDeleteConfirm = (): void => {
    if (selectedTeacher) {
      dispatch(deleteTeacher(selectedTeacher.id));
    }
  };

  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "create" && status === 201) handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();

    dispatch(resetTeacherServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  const columns = useMemo<TableColumn<TeacherSummaryDto>[]>(
    () => [
      { id: "teacherId", label: "Teacher ID", minWidth: 120 },
      {
        id: "name",
        label: "Full Name",
        minWidth: 170,
        format: (value) => (
          <Typography
            component="span"
            sx={{ fontWeight: 600, fontSize: "0.875rem" }}
          >
            {String(value || "N/A")}
          </Typography>
        ),
      },
      { id: "email", label: "Email Address", minWidth: 200 },
      { id: "primaryPhoneNumber", label: "Phone", minWidth: 130 },
      { id: "branchName", label: "Branch", minWidth: 150 },
    ],
    [],
  );

  const renderRowActions = (row: TeacherSummaryDto): React.ReactNode => {
    const inlineActions: RowActionDef[] = [
      {
        label: "View",
        icon: <VisibilityOutlinedIcon fontSize="small" />,
        color: "info",
        onClick: () => handleOpenView(row),
        show: canRead,
      },
    ];

    const menuActions: RowActionDef[] = [
      {
        label: "Edit Profile",
        icon: <EditOutlinedIcon fontSize="small" />,
        onClick: () => handleOpenUpdate(row),
        show: canUpdate,
      },
      {
        label: "Delete Teacher",
        icon: <DeleteOutlineIcon fontSize="small" />,
        color: "error",
        onClick: () => handleOpenDelete(row),
        show: canDelete,
      },
    ];

    return (
      <RowActionMenu inlineActions={inlineActions} menuActions={menuActions} />
    );
  };

  return (
    <PageContainer
      title="Teacher Management"
      subtitle="Manage teacher profiles, qualifications, and system linkages."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={handleOpenCreate}
          >
            Add Teacher Profile
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => {
          setSearchTerm(e.target.value);
          setPageConfig((prev) => ({ ...prev, page: 1 }));
        }}
        searchPlaceholder="Search by name, ID or email..."
      >
        <BranchSelectFilter
          value={selectedBranchId}
          onChange={(bId) => {
            setSelectedBranchId(bId);
            setPageConfig((p) => ({ ...p, page: 1 }));
          }}
        />
      </PageToolbar>

      <StandardTable<TeacherSummaryDto>
        columns={columns}
        data={teachers}
        loading={loadingTeachers}
        error={errorLoadingTeachers}
        onRetry={fetchTeachersList}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(newPage) =>
          setPageConfig((prev) => ({ ...prev, page: newPage }))
        }
        onRowsPerPageChange={(newLimit) =>
          setPageConfig((prev) => ({ ...prev, limit: newLimit, page: 1 }))
        }
        rowAction={renderRowActions}
      />

      {isCreateModalOpen && (
        <CreateTeacherModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingTeacherCreate}
          error={errorCreatingTeacher}
        />
      )}

      {isUpdateModalOpen && selectedTeacher && (
        <UpdateTeacherModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          teacher={
            (teacher?.id === selectedTeacher.id
              ? teacher
              : selectedTeacher) as TeacherResponseDto
          }
          loading={loadingTeacherUpdate || loadingTeacher}
          error={errorUpdatingTeacher || errorLoadingTeacher}
        />
      )}

      {isViewModalOpen && (
        <ViewTeacherModal
          open={isViewModalOpen}
          onClose={handleCloseView}
          teacher={teacher}
          loading={loadingTeacher}
          error={errorLoadingTeacher}
        />
      )}

      {isDeleteModalOpen && selectedTeacher && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Teacher"
          description={`Are you sure you want to delete the profile for "${selectedTeacher.name}"? This action cannot be undone.`}
          loading={loadingTeacherDelete}
        />
      )}
    </PageContainer>
  );
};

export default TeachersPage;
